/**
 * useMessages Hook
 *
 * Manages message threads and individual messages from Firestore.
 */

import { useState, useEffect } from 'react';
import { db } from '../../../services/firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../services/firebase';
import { useAuth } from '../../../contexts';

export interface MessageThread {
  id: string;
  participants: string[];
  otherParticipant: {
    id: string;
    name: string;
    photo?: string;
  };
  listing?: {
    id: string;
    title: string;
    photo: string;
  };
  lastMessage: {
    content: string;
    senderId: string;
    createdAt: Date;
  };
  unreadCount: number;
  createdAt: Date;
}

export interface Message {
  id: string;
  threadId: string;
  senderId: string;
  content: string;
  createdAt: Date;
  read: boolean;
}

export const useMessages = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchThreads = async () => {
      if (!user) {
        setThreads([]);
        setIsLoading(false);
        return;
      }

      try {
        const threadsRef = collection(db, 'messageThreads');
        const q = query(
          threadsRef,
          where('participants', 'array-contains', user.uid),
          orderBy('lastMessageAt', 'desc')
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setThreads([]);
          setIsLoading(false);
          return;
        }

        const threadsData: MessageThread[] = snapshot.docs.map(doc => {
          const data = doc.data();
          const otherParticipantId = data.participants?.find((p: string) => p !== user.uid) || '';

          return {
            id: doc.id,
            participants: data.participants || [],
            otherParticipant: data.otherParticipant || {
              id: otherParticipantId,
              name: 'Unknown',
            },
            listing: data.listing,
            lastMessage: {
              content: data.lastMessage?.content || '',
              senderId: data.lastMessage?.senderId || '',
              createdAt: data.lastMessage?.createdAt?.toDate() || new Date(),
            },
            unreadCount: data.unreadCount || 0,
            createdAt: data.createdAt?.toDate() || new Date(),
          };
        });

        setThreads(threadsData);
      } catch (error) {
        console.error('Error fetching message threads:', error);
        setThreads([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThreads();
  }, [user]);

  const unreadCount = threads.reduce((sum, t) => sum + t.unreadCount, 0);

  return { threads, unreadCount, isLoading };
};

export const useMessageThread = (threadId: string | undefined) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [thread] = useState<MessageThread | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!threadId || !user) {
      setIsLoading(false);
      return;
    }

    const fetchThread = async () => {
      try {
        // Fetch messages for this thread
        const messagesRef = collection(db, 'messages');
        const q = query(
          messagesRef,
          where('threadId', '==', threadId),
          orderBy('createdAt', 'asc')
        );

        const snapshot = await getDocs(q);

        const messagesData: Message[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            threadId: data.threadId,
            senderId: data.senderId,
            content: data.content || '',
            createdAt: data.createdAt?.toDate() || new Date(),
            read: data.read || false,
          };
        });

        setMessages(messagesData);
      } catch (error) {
        console.error('Error fetching messages:', error);
        setMessages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThread();
  }, [threadId, user]);

  const sendMessage = async (content: string): Promise<Message> => {
    if (!user || !threadId) throw new Error('Cannot send message');

    // All sends go through the moderated Cloud Function. The function
    // throws a friendly HttpsError with the user-facing message if blocked
    // (phone, email, social handles, etc.) - let it propagate so the UI
    // can toast it.
    const fn = httpsCallable<
      { threadId: string; content: string },
      { success: boolean; messageId: string }
    >(functions, 'sendMessage');
    const result = await fn({ threadId, content });

    const newMessage: Message = {
      id: result.data.messageId,
      threadId,
      senderId: user.uid,
      content,
      createdAt: new Date(),
      read: false,
    };

    setMessages((prev) => [...prev, newMessage]);
    return newMessage;
  };

  return { thread, messages, isLoading, sendMessage };
};

export default useMessages;
